import { useMemo } from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  CustomAutoComplete,
  CustomTextField,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../common';
import { useUnits } from '../../../hooks/useUnits';
import { useInventoryArticleSearch } from '../../internal-orders/hooks/useInventoryArticleSearch';

const SupplyOrderMaterialsSection = ({
  materials = [],
  searchEnabled = true,
  onAddMaterial,
  onUpdateMaterialQuantity,
  onRemoveMaterial,
}) => {
  const { data: units = [] } = useUnits({ enabled: searchEnabled });
  const {
    searchTerm,
    setSearchTerm,
    materialOptions,
    isSearching,
    isPendingDebounce,
    hasSearchError,
    resetSearch,
    minSearchLength,
  } = useInventoryArticleSearch(units, searchEnabled);

  const addedMaterialIds = useMemo(
    () => new Set(materials.map((item) => item.materialId)),
    [materials]
  );

  const availableOptions = useMemo(() => {
    if (isPendingDebounce) return [];
    return materialOptions.filter((option) => !addedMaterialIds.has(option.value));
  }, [materialOptions, addedMaterialIds, isPendingDebounce]);

  const handleMaterialSelect = (_, option) => {
    if (!option) return;
    onAddMaterial(option);
    resetSearch();
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        Artículos
      </Typography>

      <Box sx={{ mb: 2 }}>
        <CustomAutoComplete
          label="Buscar artículo"
          options={availableOptions}
          value={null}
          inputValue={searchTerm}
          onInputChange={(_, value) => setSearchTerm(value)}
          onChange={handleMaterialSelect}
          loading={isSearching}
          disabled={!searchEnabled}
          noOptionsText={
            hasSearchError
              ? 'Error al buscar artículos'
              : isPendingDebounce
                ? 'Buscando...'
                : searchTerm.trim().length > 0 &&
                    searchTerm.trim().length < minSearchLength
                  ? `Escribí al menos ${minSearchLength} caracteres para buscar`
                  : searchTerm.trim().length >= minSearchLength
                    ? 'Sin resultados'
                    : 'Código, código externo o descripción (mín. 3 caracteres)'
          }
        />
      </Box>

      <GeneralTableBox>
        <Table size="small">
          <TableHead>
            <TableRow>
              <HeadTableCell>Artículo</HeadTableCell>
              <HeadTableCell>Unidad</HeadTableCell>
              <HeadTableCell>Cantidad</HeadTableCell>
              <HeadTableCell align="center">Acciones</HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {materials.length === 0 ? (
              <TableRow>
                <RowTableCell colSpan={4} sx={{ textAlign: 'center', py: 3 }}>
                  Agregá al menos un artículo
                </RowTableCell>
              </TableRow>
            ) : (
              materials.map((material) => (
                <TableRow key={material.materialId}>
                  <RowTableCell>
                    {material.code ? `${material.code} — ` : ''}
                    {material.name}
                  </RowTableCell>
                  <RowTableCell>{material.unit || '-'}</RowTableCell>
                  <RowTableCell>
                    <CustomTextField
                      type="number"
                      value={material.quantity}
                      onChange={(event) =>
                        onUpdateMaterialQuantity(material.materialId, event.target.value)
                      }
                      inputProps={{ min: 0, step: '0.001' }}
                      size="small"
                      sx={{ maxWidth: 120 }}
                    />
                  </RowTableCell>
                  <RowTableCell align="center">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onRemoveMaterial(material.materialId)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </RowTableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>
    </Box>
  );
};

export default SupplyOrderMaterialsSection;
